import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { formatDueDaysForDisplay } from 'shared/utils/invoicing';

import { CollapsibleContainer } from '@/components';
import ConnectProviderWarningModal from '@/components/ConnectProviderWarningModal';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useArchivedInvoicesCount } from '@/hooks/useArchivedInvoicesCount';
import { ROUTE } from '@/router/routes';

const buttonStyles = {
    alignSelf: 'center',
    width: 200,
};

function InvoicingAndAdministrationSection() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { isInvoicingAvailable, isInvoiceSystemEnabled } =
        useInvoicingSystemContext();
    const { userOrganisation } = useOrganisationsContext();

    // Internal-invoicing settings belong to Web Invoicing, so they follow the
    // regional availability gate as well as the invoicing-system flag.
    const showInvoiceSystemSettings =
        isInvoicingAvailable && isInvoiceSystemEnabled;

    const defaultDueDaysDisplay = formatDueDaysForDisplay(
        userOrganisation?.default_invoice_due_days,
    );

    const { count: archivedInvoicesCount } = useArchivedInvoicesCount();

    const [isProviderWarningModalVisible, setProviderWarningModalVisible] =
        useState(false);

    const navigateToConnectProvider = () => {
        navigate(ROUTE.accountingProviders);
    };

    const handleConnectToProviderClick = () => {
        isInvoiceSystemEnabled
            ? setProviderWarningModalVisible(true)
            : navigateToConnectProvider();
    };

    const renderSection = ({
        headerText,
        explanationText,
        actionText,
        testId,
        route,
        onClick,
    }: {
        headerText: string;
        explanationText: string;
        actionText: string;
        testId: string;
        route?: string;
        onClick?: () => void;
    }) => (
        <Box sx={{ display: 'flex', gap: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    flexDirection: 'column',
                    flex: 2,
                }}
            >
                <Typography fontWeight="bold" fontSize={14}>
                    {headerText}
                </Typography>
                <Typography fontSize={14}>{explanationText}</Typography>
            </Box>
            <Button
                {...(route ? { component: Link, to: route } : { onClick })}
                variant="contained"
                sx={buttonStyles}
                data-test-id={testId}
            >
                {actionText}
            </Button>
        </Box>
    );

    return (
        <CollapsibleContainer
            title={t('Options:button:invoicing_administration')}
            testID="SettingsPage-InvoicingAndAdministration"
        >
            <Alert severity="info" sx={{ mb: 3 }}>
                {t('Settings:invoices_admin:heading_explanation')}
            </Alert>

            {showInvoiceSystemSettings && archivedInvoicesCount > 0 && (
                <>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexDirection: 'column',
                                flex: 2,
                            }}
                        >
                            <Typography fontWeight="bold" fontSize={14}>
                                {t('Invoicing:archived_invoices')}
                            </Typography>
                            <Typography fontSize={14}>
                                {t('Invoicing:archived_invoices_description')}
                            </Typography>
                        </Box>
                        <Button
                            component={Link}
                            to={ROUTE.archivedInvoices}
                            sx={{
                                alignSelf: 'center',
                                width: 200,
                                textTransform: 'none',
                            }}
                            endIcon={<ArrowForwardIcon />}
                            data-test-id="InvoicingAndAdministrationSection-ArchivedInvoicesLink"
                        >
                            {t('Invoicing:archived_invoices')} (
                            {archivedInvoicesCount})
                        </Button>
                    </Box>
                    <Divider sx={{ my: 4 }} />
                </>
            )}

            {showInvoiceSystemSettings && (
                <>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexDirection: 'column',
                                flex: 2,
                            }}
                        >
                            <Typography fontWeight="bold" fontSize={14}>
                                {t(
                                    'InvoiceSystem:business_details:actions:manage_details',
                                )}
                            </Typography>
                            <Typography fontSize={14}>
                                {t(
                                    'InvoiceSystem:business_details:business_details_disclaimer',
                                )}
                            </Typography>
                        </Box>
                        <Button
                            component={Link}
                            to={ROUTE.businessDetails}
                            variant="contained"
                            sx={buttonStyles}
                            data-test-id="InvoicingAndAdministrationSection-BusinessDetailsButton"
                        >
                            {t(
                                'InvoiceSystem:business_details:actions:manage_details',
                            )}
                        </Button>
                    </Box>
                    <Divider sx={{ my: 4 }} />
                </>
            )}

            {showInvoiceSystemSettings && (
                <>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexDirection: 'column',
                                flex: 2,
                            }}
                        >
                            <Typography fontWeight="bold" fontSize={14}>
                                {t(
                                    'Settings:invoices_admin:sales_taxes_header',
                                )}
                            </Typography>
                            <Typography fontSize={14}>
                                {t(
                                    'Settings:invoices_admin:sales_taxes_explanation',
                                )}
                            </Typography>
                        </Box>
                        <Button
                            component={Link}
                            to={ROUTE.salesTaxes}
                            variant="contained"
                            sx={buttonStyles}
                            data-test-id="InvoicingAndAdministrationSection-SalesTaxesButton"
                        >
                            {t('Settings:invoices_admin:sales_taxes_action')}
                        </Button>
                    </Box>
                    <Divider sx={{ my: 4 }} />
                </>
            )}

            <Box sx={{ display: 'flex', gap: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexDirection: 'column',
                        flex: 2,
                    }}
                >
                    <Typography fontWeight="bold" fontSize={14}>
                        {t(
                            'Settings:invoices_admin:configure_procedures_header',
                        )}
                    </Typography>
                    <Typography fontSize={14}>
                        {t(
                            'Settings:invoices_admin:configure_procedures_explanation',
                        )}
                    </Typography>
                </Box>
                <Button
                    component={Link}
                    to={ROUTE.procedures}
                    variant="contained"
                    sx={buttonStyles}
                    data-test-id="InvoicingAndAdministrationSection-ConfigureProceduresButton"
                >
                    {t('Settings:invoices_admin:configure_procedures_action')}
                </Button>
            </Box>
            <Divider sx={{ my: 4 }} />

            {showInvoiceSystemSettings && (
                <>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexDirection: 'column',
                                flex: 2,
                            }}
                        >
                            <Typography fontWeight="bold" fontSize={14}>
                                {t(
                                    'InvoiceSystem:business_details:products_settings',
                                )}
                            </Typography>
                            <Typography fontSize={14}>
                                {t(
                                    'InvoiceSystem:business_details:products_settings_disclaimer',
                                )}
                            </Typography>
                        </Box>
                        <Button
                            component={Link}
                            to={ROUTE.invoicingProducts}
                            variant="contained"
                            sx={buttonStyles}
                            data-test-id="InvoicingAndAdministrationSection-ProductsButton"
                        >
                            {t(
                                'InvoiceSystem:business_details:actions:manage_products',
                            )}
                        </Button>
                    </Box>
                    <Divider sx={{ my: 4 }} />
                </>
            )}

            {isInvoicingAvailable && (
                <>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexDirection: 'column',
                                flex: 2,
                            }}
                        >
                            <Typography fontWeight="bold" fontSize={14}>
                                {t(
                                    'Settings:invoices_admin:connect_provider_header',
                                )}
                            </Typography>
                            <Typography fontSize={14}>
                                {t(
                                    'Settings:invoices_admin:connect_provider_explanation_xero_only',
                                )}
                            </Typography>
                        </Box>
                        <Button
                            onClick={handleConnectToProviderClick}
                            variant="contained"
                            sx={buttonStyles}
                            data-test-id="InvoicingAndAdministrationSection-ConnectToProviderButton"
                        >
                            {t(
                                'Settings:invoices_admin:connect_provider_action',
                            )}
                        </Button>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {renderSection({
                        headerText: t(
                            'Settings:invoices_admin:default_due_days_header',
                        ),
                        explanationText: t(
                            'Settings:invoices_admin:default_due_days_description',
                            {
                                value: defaultDueDaysDisplay.dueValue,
                                unit: t(
                                    `Invoice:time_options:${defaultDueDaysDisplay.unit}`,
                                ),
                            },
                        ),
                        actionText: t(
                            'Settings:invoices_admin:default_due_days_action',
                        ),
                        testId: 'InvoicingAndAdministrationSection-DefaultDueDaysButton',
                        route: ROUTE.defaultInvoiceDueDays,
                    })}
                </>
            )}

            {isProviderWarningModalVisible && (
                <ConnectProviderWarningModal
                    onCancel={() => setProviderWarningModalVisible(false)}
                    onProceed={() => {
                        setProviderWarningModalVisible(false);
                        navigateToConnectProvider();
                    }}
                />
            )}
        </CollapsibleContainer>
    );
}

export default InvoicingAndAdministrationSection;
